/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type RefObject, forwardRef, type Ref } from 'react';
import type {
	TreeItemContainerProps,
	TreeItemHeadProps,
} from '@jira/polaris__lib-tree/src/TreeItemBase.tsx';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { ExpandableMenuItemContent } from '@atlaskit/side-nav-items/expandable-menu-item';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { DraggableRefContext } from '../../../../common/utils/draggable-context/index.tsx';
import type { JpdTreeItem } from '../../../../common/utils/tree/index.tsx';

export const TreeItemParentContainer: React.ForwardRefExoticComponent<
	{
		children?: React.ReactNode;
		role: string;
	} & React.RefAttributes<HTMLElement>
> = forwardRef(
	(
		{ children, role }: { children?: React.ReactNode; role: string },
		ref: React.Ref<HTMLElement>,
	) => {
		if (role === 'group') {
			return (
				// oxlint-disable-next-line typescript/consistent-type-assertions
				<ExpandableMenuItemContent ref={ref as RefObject<HTMLDivElement>}>
					{children}
				</ExpandableMenuItemContent>
			);
		}
		return <>{children}</>;
	},
);

export const TreeItemContainer: React.ForwardRefExoticComponent<
	{
		item: JpdTreeItem;
		indentPerLevel: number;
		currentLevel: number;
		isExpanded?: boolean;
	} & {
		children?: React.ReactNode | undefined;
	} & React.RefAttributes<HTMLElement>
> = forwardRef(
	<TItem extends JpdTreeItem>(
		{ children, item }: TreeItemContainerProps<TItem>,
		ref: Ref<HTMLElement>,
	) => {
		if (item.data?.type === 'placeholder') {
			return <>{children}</>;
		}
		if (item.hasChildren) {
			return (
				<>
					<MenuListItem>
						<Box
							ref={ref}
							data-expand-state={item.isExpanded ? 'expanded' : 'collapsed'}
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.menu-items.views.tree-wrapper.viewset-container-item"
							xcss={positionRelative}
						>
							{children}
						</Box>
					</MenuListItem>
				</>
			);
		}
		return (
			<>
				<MenuListItem
					// oxlint-disable-next-line typescript/consistent-type-assertions
					ref={ref as RefObject<HTMLDivElement>}
				>
					{children}
				</MenuListItem>
			</>
		);
	},
);

export const TreeItemHead: React.ForwardRefExoticComponent<
	{
		ariaLabel?: string;
		item: JpdTreeItem;
	} & {
		children?: React.ReactNode | undefined;
	} & React.RefAttributes<HTMLElement>
> = forwardRef(
	<TItem extends JpdTreeItem>(
		{ children, item }: TreeItemHeadProps<TItem>,
		ref: Ref<HTMLElement>,
	) => {
		if (item.data?.type === 'placeholder') {
			return <>{children}</>;
		}
		if (item.hasChildren) {
			return (
				<>
					<Box
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.menu-items.views.tree-wrapper.viewset-item"
						data-component-selector="navigation-section-item"
					>
						<DraggableRefContext.Provider value={ref}>{children}</DraggableRefContext.Provider>
					</Box>
				</>
			);
		}
		return (
			<>
				<Box
					testId={`navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.menu-items.views.view-item-${item.data?.view?.polarisId}`}
					data-component-selector="navigation-view-item"
					xcss={positionRelative}
				>
					<DraggableRefContext.Provider value={ref}>{children}</DraggableRefContext.Provider>
				</Box>
			</>
		);
	},
);

const positionRelative = xcss({ position: 'relative' });
